import { Injectable, APP_INITIALIZER, inject } from '@angular/core';
import { HttpClient, HttpBackend } from '@angular/common/http';

import { handleError } from '../app/modules/core/services/service.helpers';

import * as _ from 'lodash';
import { WarningMessageType } from 'src/app/modules/shared/models';
import { environment } from 'src/environments/environment';
import { firstValueFrom, of, map, catchError } from 'rxjs';

/**
 * Service that will read the configuration before load
 * and then save in this service.
 * Inject the service to read all the configurations
 * The config.json is for the dev-environment.
 * Inspiration from here: https://stackoverflow.com/questions/42110817/load-config-json-file-in-angular-2
 */

@Injectable()
export class ConfigService {
    private _config: any;

    private readonly _http: HttpClient;


    // DO this so that that we create a new httpClient that not will
    // be transformed using the httpInterceptor
    constructor() {
        const handler = inject(HttpBackend);

        this._http = new HttpClient(handler);
    }

    public load() {
        let config = '/config/config.json'
        if (environment.utv) {
            config = '/config/config.utv.json';
        }
        const configuration = firstValueFrom(this._http.get(config).pipe(
            map((config: any) => {
                this._config = config;
                return config;
            }),
            catchError(error => {
                handleError(error);
                return of(undefined);
            })
        ));

        return Promise.all([configuration]);
    }

    // here goes every variable from the config-json
    // important that this has the same key as the azure-deploy.parameters.json
    get mapboxAccessToken() {
        return this._config['MAPBOX_ACCESS_TOKEN'];
    }
    get signalRHub() {
        return this._config['SIGNALR_HUB'];
    }
    get signalRScope() {
        return this._config['SIGNALR_SCOPE'];
    }
    get applicationInsightsInstrumentationKey() {
        return this._config['APPLICATION_INSIGHTS_INSTRUMENTATION_KEY'];
    }
    get mapApiBaseUrl() {
        return this._config['MAP_API_BASE_URL'];
    }

    get mapApiScope() {
        return this._config['MAP_API_SCOPE'];
    }
    get mapRouteTilesetUrl() {
        return this._config['MAP_ROUTE_TILESET_URL'];
    }
    get mapRouteTilesetName() {
        return this._config['MAP_ROUTE_TILESET_NAME'];
    }
    get mapStopsTilesetUrl() {
        return this._config['MAP_STOPS_TILESET_URL'];
    }
    get mapStopsTilesetName() {
        return this._config['MAP_STOPS_TILESET_NAME'];
    }
    get environmentName() {
        const env: string = this._config['ENVIRONMENT_NAME'];
        if (env.toLowerCase() === 'prod') {
            return '';
        }
        return this._config['ENVIRONMENT_NAME'];
    }
    get taApiBaseUrl() {
        return this._config['TA_API_BASE_URL'];
    }
    get cmApiBaseUrl() {
        return this._config['CM_API_BASE_URL'];
    }
    get taApiScope() {
        return this._config['TA_API_SCOPE'];
    }
    get cmApiScope() {
        return this._config['CM_API_SCOPE'];
    }
    get minuteFilterForFetchingWarningData() {
        return this.config['MINUTE_FILTER_FOR_FETCHING_WARNING_DATA'];
    }
    get enableSignOnFeature(): boolean {
        return true;
        // const value = this._config['FT_ENABLE_SIGNON'];
        // return value === 'true' || value === true;
    }
    //#region MSAL settings
    get msalClientId() {
        return this._config['MSAL_AUTH_CLIENTID'];
    }
    get msalAuthority() {
        return this._config['MSAL_AUTH_AUTHORITY'];
    }
    get msalConsentScopes() {
        const scopes = this._config['MSAL_CONSENT_SCOPES'];
        return this._constructArrayFromString(scopes);
    }
    get contentScopes() {
        const scopes = this._config['MSAL_SCOPES_CONTENT'];
        return this._constructArrayFromString(scopes);
    }
    //#endregion

    get config() {
        return this._config;
    }

    get disabledWarningTypes(): WarningMessageType[] {
        const warningString = this._config['DISABLED_WARNING_TYPES'];
        if (warningString) {
            const warnings = this._constructArrayFromString(warningString);
            const value = _.map(warnings, w => parseWarningMessageType(w));
            return value;
        }
        return [];
    }

    get signonValidGarageCodes(): string[] {
        const codes = this._config['SIGNON_VALID_GARAGE_CODES'];
        if (codes) {
            return this._constructArrayFromString(codes)
                .map(c => (c || '').trim())
                .filter(c => !!c);
        }
        return [];
    }

    get signonGarageCodePrincipalMap(): Map<string, string> {
        const raw = this._config['SIGNON_GARAGE_CODE_PRINCIPAL_MAP'];
        const map = new Map<string, string>();
        if (raw) {
            this._constructArrayFromString(raw).forEach(entry => {
                const eqIndex = entry.indexOf('=');
                if (eqIndex > 0) {
                    const code = entry.substring(0, eqIndex).trim();
                    const label = entry.substring(eqIndex + 1).trim();
                    if (code && label) {
                        map.set(code, label);
                    }
                }
            });
        }
        return map;
    }

    get deepLinkAllowedOrigins(): string[] {
        const origins = this._config['DEEP_LINK_ALLOWED_ORIGINS'];
        if (origins) {
            return this._constructArrayFromString(origins)
                .map(o => (o || '').trim())
                .filter(o => !!o);
        }
        return [];
    }

    get passengerInformationBaseUrl(): string {
        return this._config['PASSENGER_INFORMATION_BASE_URL'];
    }

    private _constructArrayFromString(value: string) {
        return _.split(value, ';');
    }
}

export function ConfigFactory(config: ConfigService) {
    return () => config.load();
}

export function init() {
    return {
        provide: APP_INITIALIZER,
        useFactory: ConfigFactory,
        deps: [ConfigService],
        multi: true
    };
}

const ConfigModule = {
    init: init
};

export { ConfigModule };


export function parseWarningMessageType(value: string): WarningMessageType {
    return WarningMessageType[value as keyof typeof WarningMessageType];
}
